<template>
  <div v-if="chartingData">
    <div v-if="usesCorrectedViscosityRate" class="flex justify-content-end">
      <div class="flex text-xs p-1">* Uses corrected viscosity rate</div>
    </div>

    <div class="break-inside-avoid">
      <PumpTornadoHeadChart
        :key="headTornadoKey"
        :aspect-ratio="2"
        draw-legend
        is-horizontal
        is-static
        :show-pump-table="false"
        :title="scenario?.title || ''"
        :show-operating-frequency="false"
        :min-frequency="minFrequency"
        :max-frequency="maxFrequency"
        x-legend-label="In Situ Flow Rate"
        y-legend-label="TDH"
        :scenario="props.scenario"
        :scenario-calculations="props.scenarioCalculations"
        :pump-tornado-chart-data="chartingData"
      />
    </div>
    <div class="break-inside-avoid">
      <PumpTornadoBHPChart
        :key="bhpTornadoKey"
        :aspect-ratio="2"
        draw-legend
        is-horizontal
        is-static
        :title="scenario?.title || ''"
        :show-operating-frequency="false"
        :min-frequency="minFrequency"
        :max-frequency="maxFrequency"
        x-legend-label="In Situ Flow Rate"
        y-legend-label="Req BHP"
        :scenario="props.scenario"
        :scenario-calculations="props.scenarioCalculations"
        :pump-tornado-chart-data="chartingData"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import PumpTornadoHeadChart from "./PumpTornadoHeadChart.vue";
import PumpTornadoBHPChart from "./PumpTornadoBHPChart.vue";
import { usePumpChartingData } from "@/libs/charting";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { Scenario } from "@/composables/forms/formTypes";
import { sizingKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { injectStrict } from "@/utils/utils";
import { v4 } from "uuid";

const props = defineProps<{
  chartMinHeight: number;
  minFrequency: number;
  maxFrequency: number;
  scenario?: Scenario | null;
  scenarioCalculations: ScenarioCalculations;
}>();

const headTornadoKey = ref(v4());
const bhpTornadoKey = ref(v4());
watch(
  () => [props.minFrequency, props.maxFrequency],
  () => {
    headTornadoKey.value = v4();
    bhpTornadoKey.value = v4();
  }
);

const { primaryPump } = props.scenarioCalculations;
const usesCorrectedViscosityRate = computed(
  () =>
    primaryPump.value?.rate_correction_ratio !== 0 &&
    primaryPump.value?.rate_correction_ratio !== 1
);

const _sizing = injectStrict(sizingKey, null);
const {
  getLiquidFlowUnit,
  getHydraulicPowerUnit,
  getLargeLengthUnit,
  getPressureUnit
} = useScenarioUnits(props.scenario, _sizing);

const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetPowerUnit = getHydraulicPowerUnit("Shaft Limit.convert.hp");
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);

const chartingData = computed(() =>
  usePumpChartingData(
    props.minFrequency,
    props.maxFrequency,
    props.scenarioCalculations,
    targetLiquidFlowUnit.value,
    targetPowerUnit.value,
    targetLargeLengthUnit.value,
    targetPressureUnit.value
  )
);
</script>
